<template>
  <Head :title="`ShoutOut: Book ${vip.first_name} ${vip.last_name}`">
    <meta
      name="description"
      :content="`ShoutOut: Book ${vip.first_name} ${vip.last_name}`"
    >
    <link
      rel="canonical"
      :href="localeRoute('frontend.booking.show', [vip])"
    >

    <meta
      property="og:type"
      content="website"
    >
    <meta
      property="og:url"
      :content="localeRoute('frontend.booking.show', [vip])"
    >
    <meta
      property="og:title"
      :content="vip.content.display_name"
    >
    <meta
      property="og:description"
      :content="`ShoutOut: Book ${vip.first_name} ${vip.last_name}`"
    >
    <meta
      v-if="vip.content.poster?.url"
      property="og:image"
      :content="vip.content.poster.url"
    >
    <meta
      name="twitter:card"
      content="summary_large_image"
    >
    <meta
      property="twitter:domain"
      content="shoutout.vip"
    >
    <meta
      property="twitter:url"
      :content="localeRoute('frontend.vips.show', [vip])"
    >
    <meta
      name="twitter:title"
      :content="vip.content.display_name"
    >
    <meta
      name="twitter:description"
      :content="`ShoutOut: Book ${vip.first_name} ${vip.last_name}`"
    >
    <meta
      v-if="vip.content.poster?.url"
      name="twitter:image"
      :content="vip.content.poster.url"
    >
  </Head>

  <section class="vip-details relative pt-4 pb-8 lg:pb-20">
    <template v-if="$page.props.darkModeEnabled">
      <img
        src="/frontend/assets/mobile-hero-swirl-white.svg"
        class="absolute top-0 left-0 lg:hidden"
      >
      <img
        src="/frontend/assets/desktop-hero-swirl-white.svg"
        class="absolute top-0 left-0 hidden lg:block"
      >
    </template>
    <template v-else>
      <img
        src="/frontend/assets/mobile-hero-swirl.svg"
        class="absolute top-0 left-0 lg:hidden"
      >
      <img
        src="/frontend/assets/desktop-hero-swirl.svg"
        class="absolute top-0 left-0 hidden lg:block"
      >
    </template>

    <div class="container">
      <div class="flex flex-wrap mt-12">
        <div class="w-full relative z-20 md:w-2/5 lg:w-5/12">
          <div class="relative">
            <div
              v-if="vip.content.video"
              class="w-full shadow-vip-video rounded-lg overflow-hidden"
            >
              <VideoPlayer
                controls
                :autoplay="false"
                :poster="vip.content.poster?.url"
                :src="vip.content.video.url"
                width="524"
                height="524"
              />
            </div>
            <img
              v-else-if="vip.content.image"
              :src="vip.content.image.url"
              :alt="vip.content.display_name"
              class="w-full shadow-vip-video rounded-lg"
              width="524"
              height="524"
            >

            <span
              v-if="!vip.content.available"
              class="bg-black/50 absolute z-40 left-0 right-0 top-0 bottom-0 rounded-lg text-white"
            />
            <span
              v-if="!vip.content.available"
              class="text-white block text-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50"
            >
              {{ __('general.unavailable') }}
            </span>
          </div>

          <div class="mt-8 mx-auto text-center dark:text-white">
            Powered by: <img
              src="../../../assets/jan-vis-logo-rood.svg"
              class="inline-block w-12 h-12"
            >
          </div>

          <div
            v-if="vip.links.length > 0"
            class="hidden lg:block space-x-1.5 mt-6"
          >
            <span class="text-sm text-gray-300 dark:text-white">
              {{ __('booking-vip.social-media', {vip: vip.firstname}) }}
            </span>

            <a
              v-for="link in vip.links"
              :key="`vip-link-${link.type}`"
              :href="link.link"
              target="_blank"
            >
              <!-- @todo dark -->
              <img
                :src="$page.props.darkModeEnabled ? `/frontend/assets/${link.type}-white.svg` : `/frontend/assets/${link.type}-black.svg`"
                :alt="link.type"
                class="inline"
              >
            </a>
          </div>
        </div>

        <div class="w-full mt-8 lg:w-7/12 md:w-3/5 md:pl-12 lg:pl-12 md:mt-0">
          <div class="space-x-1 space-y-1">
            <span
              v-if="vip.is_new"
              class="border text-sm font-medium py-0.5 px-2 rounded border-red text-red"
            >
              {{ __('general.tags.new') }}
            </span>
            <span
              v-if="vip.content.offers_48h"
              class="border text-sm font-medium py-0.5 px-2 rounded border-red text-red"
            >
              {{ __('general.tags.48hr') }}
            </span>
            <Link
              v-for="(category, i) in vip.content.categories"
              :key="`vip-${vip.username}-category-${i}`"
              class="border text-sm font-medium py-0.5 px-2 rounded border-gray-300 text-gray-300 dark:border-white dark:text-white"
              :href="localeRoute('frontend.categories.slug', [category.slug])"
            >
              {{ category.name }}
            </Link>
          </div>

          <h1 class="text-4xl font-bold mt-3 lg:text-6xl dark:text-white">
            {{ vip.content.display_name }}
          </h1>

          <span
            v-if="vip.content.price > 0"
            class="block text-xl font-bold mt-2 lg:text-2xl dark:text-white"
          >
            &euro; {{ vip.content.price / 100 }}
          </span>

          <div class="relative">
            <!-- eslint-disable vue/no-v-html -->
            <div
              ref="bio"
              class="border-t border-black text-gray-300 pt-3 font-medium lg:mb-0 dark:text-gray-100"
              :class="{'line-clamp-3': !readMore}"
              v-html="vip.content.about"
            />
            <!-- eslint-enable vue/no-v-html -->
            <button
              v-if="readMoreButton && !readMore"
              type="button"
              class="text-red hover:underline"
              @click="readMore = true"
            >
              {{ __('general.read-more') }}
            </button>
          </div>

          <div
            v-if="vip.links.length > 0"
            class="space-x-1.5 lg:hidden"
          >
            <span class=" text-sm text-gray-300 dark:text-white">
              {{ __('booking-vip.social-media', {vip: vip.firstname}) }}
            </span>

            <a
              v-for="link in vip.links"
              :key="`vip-link-${link.type}`"
              :href="link.link"
              target="_blank"
            >
              <img
                :src="$page.props.darkModeEnabled ? `/frontend/assets/${link.type}-white.svg` : `/frontend/assets/${link.type}-black.svg`"
                :alt="link.type"
                class="inline"
              >
            </a>
          </div>

          <div
            v-if="vip.content.available"
            class="flex flex-wrap lg:mt-1"
          >
            <div
              v-if="success"
              class="text-center border-b border-gray-300 pb-2 mt-4 dark:text-red"
            >
              <h2 class="text-lg">
                {{ __('booking-vip.form.success.title') }}
              </h2>

              <p class="text-lg">
                {{ __('booking-vip.form.success.text') }}
              </p>
            </div>

            <form
              v-if="!success"
              @submit.prevent="form.post(localeRoute('frontend.booking.request', vip), {
                onSuccess: () => form.reset(),
                onError: () => {
                  form.recaptcha = '';
                  if ($refs.recaptcha) {
                    $refs.recaptcha.reset();
                  }
                },
              })"
            >
              <div class="flex flex-wrap mt-4">
                <div class="w-full md:w-1/2 mb-4 md:pr-2">
                  <InputField
                    v-model="form.name"
                    :form="form"
                    name="name"
                    type="text"
                    :label="__('booking-vip.form.name.label')"
                    :placeholder="__('booking-vip.form.name.placeholder')"
                  />
                </div>

                <div class="w-full md:w-1/2 mb-4 md:pl-2">
                  <InputField
                    v-model="form.email"
                    :form="form"
                    name="email"
                    type="email"
                    :label="__('booking-vip.form.email.label')"
                    :placeholder="__('booking-vip.form.email.placeholder')"
                  />
                </div>

                <div class="w-full mb-4">
                  <InputField
                    v-model="form.phone"
                    :form="form"
                    name="phone"
                    type="tel"
                    :label="__('booking-vip.form.phone.label')"
                    :placeholder="__('booking-vip.form.phone.placeholder')"
                    :is-required="false"
                  />
                </div>

                <div class="w-full mb-4">
                  <TextareaField
                    v-model="form.message"
                    :form="form"
                    name="message"
                    :label="__('booking-vip.form.message.label')"
                    :placeholder="__('booking-vip.form.message.placeholder')"
                    rows="8"
                  />
                </div>

                <div class="mb-4">
                  <VueRecaptcha
                    ref="recaptcha"
                    :sitekey="recaptchaSiteKey"
                    @verify="(response) => form.recaptcha = response"
                    @expired="() => form.recaptcha = ''"
                  />

                  <p
                    v-if="form.errors.recaptcha"
                    class="text-red text-xs italic"
                  >
                    {{ __('booking-vip.form.recaptcha-invalid') }}
                  </p>
                </div>

                <div class="w-full text-center">
                  <button
                    type="submit"
                    class="button-orange shadow-button-black dark:shadow-button-red w-auto inline-block !px-16"
                  >
                    {{ __('booking-vip.form.send') }}
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div
            v-if="!vip.content.available"
            class="mt-6 text-red font-bold text-xl"
          >
            {{ __('booking-vip.unavailable') }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {useForm} from '@inertiajs/vue3';
import {VueRecaptcha} from 'vue-recaptcha';
import VideoPlayer from '../../Components/VideoPlayer.vue';
import InputField from '../../Components/Form/InputField.vue';
import TextareaField from '../../Components/Form/TextareaField.vue';
import GoogleTagManager from '../../Mixins/GoogleTagManager';

export default {
  components: {
    VideoPlayer,
    VueRecaptcha,
    InputField,
    TextareaField,
  },

  mixins: [GoogleTagManager],

  props: {
    vip: {
      type: Object,
      required: true,
    },

    recaptchaSiteKey: {
      type: String,
      required: true,
    },

    success: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup() {
    const form = useForm({
      name: '',
      email: '',
      phone: '',
      message: '',
      recaptcha: '',
    });

    return {form};
  },

  data() {
    return {
      readMore: false,
      readMoreButton: false,
    };
  },

  mounted () {
    this.gtmViewItem(this.vip);

    if (
      this.$refs.bio.offsetHeight < this.$refs.bio.scrollHeight
      || this.$refs.bio.offsetWidth < this.$refs.bio.scrollWidth
    ) {
      this.readMoreButton = true;
    } else {
      this.readMoreButton = false;
    }
  },
};
</script>
